import { Holiday, Weekday, type IHolidayDate } from '@/constructs/Holiday';
import config from '../config';

/**
 * A representation of the months of the year. This is 0-indexed
 * (0 = January, 11 = December), to align with JavaScript Date objects.
 *
 * This is an internal `const enum` to improve readability in this file without
 * increasing the bundle size or making it globally available.
 */
const enum Month {
  JANUARY = 0,
  FEBRUARY = 1,
  MARCH = 2,
  APRIL = 3,
  MAY = 4,
  JUNE = 5,
  JULY = 6,
  AUGUST = 7,
  SEPTEMBER = 8,
  OCTOBER = 9,
  NOVEMBER = 10,
  DECEMBER = 11
}

/**
 * The current year for which the holiday dates are valid. This is
 * created as a constant to be reused in the holiday configuration, in case
 * site/locales vary by year.
 *
 * **IMPORTANT**: This should be updated annually to ensure
 * the holiday dates remain accurate, as long as the holidays
 * mapping.
 *
 * @todo As a tech debt, we want to automate dynamic holidays
 * calculations, so we don't have to update this every year.
 */
const CURRENT_YEAR = 2026;

/**
 * A mapping of supported US holidays to their respective dates.
 *
 * _Note_: This is a partial list, meaning that not all holidays
 * defined in the @link {Holiday} enum need to be supported, in this case
 * only the supported US holidays are listed.
 */
const US_HOLIDAYS: Record<Partial<Holiday>, Omit<IHolidayDate, 'holiday'>> = {
  // #region Fixed Supported US Holidays
  [Holiday.NEW_YEARS_DAY]: { day: 1, month: Month.JANUARY },
  [Holiday.JUNETEENTH_NATIONAL_INDEPENDENCE_DAY]: {
    day: 19,
    month: Month.JUNE
  },
  [Holiday.INDEPENDENCE_DAY]: { day: 4, month: Month.JULY },
  [Holiday.VETERANS_DAY]: { day: 11, month: Month.NOVEMBER },
  [Holiday.CHRISTMAS_DAY]: { day: 25, month: Month.DECEMBER },
  // #endregion

  // #region Dynamic Supported US Holidays
  [Holiday.LABOR_DAY]: {
    day: 7,
    month: Month.SEPTEMBER,
    dynamic: true,
    weekday: Weekday.MONDAY
  },
  [Holiday.THANKSGIVING_DAY]: {
    day: 26,
    month: Month.NOVEMBER,
    dynamic: true,
    weekday: Weekday.THURSDAY
  },
  [Holiday.MARTIN_LUTHER_KING_JR_DAY]: {
    day: 19,
    month: Month.JANUARY,
    dynamic: true,
    weekday: Weekday.MONDAY
  },
  [Holiday.MEMORIAL_DAY]: {
    day: 25,
    month: Month.MAY,
    dynamic: true,
    weekday: Weekday.MONDAY
  }
  // #endregion
};

/**
 * The configuration for the supported holidays for each
 * site/locale.
 *
 * It will leverage the @link {Holiday} construct to
 * map the supported holidays for each site/locale.
 */
const holiday = config({
  /** Defaults for all sites and locales. */
  default: {
    /** Default settings for all languages. */
    default: {
      /**
       * Represents the current year for which the holiday
       * dates are valid. This is important for dynamic holidays
       * that may change dates year over year.
       *
       * **IMPORTANT**: This should be updated annually to ensure
       * the holiday dates remain accurate. If the current year isn't the
       * same as the valid year, dynamic holidays may not be calculated correctly.
       */
      currentYear: CURRENT_YEAR,
      /**
       * List of supported holidays for the default site/locale.
       *
       * _Note_: This is a partial list, meaning that not all holidays
       * defined in the @link {Holiday} enum need to be supported.
       *
       * **IMPORTANT**: The actual dates of these holidays may vary by country.
       * The {@link US_HOLIDAYS} are being used as default for all sites, but
       * this should be overridden for each specific site/locale as needed.
       */
      supportedHolidays: US_HOLIDAYS
    }
  }
});

export default holiday;
